import { useState, useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { seoService } from '@/services/SEOService';
import type { InternalLinkSuggestion, ImageSuggestion, SEOImprovement } from '@/services/SEOService';
import './ContentEditor.css';

/**
 * YouTube 스크립트를 구조화된 HTML로 변환
 */
function formatYouTubeScriptToHTML(script: string): string {
  if (!script) return '';

  let html = '';
  
  // JSON 블록 찾기
  const jsonMatch = script.match(/```json\s*(\{[\s\S]*?\})\s*```/);
  
  if (jsonMatch) {
    try {
      const jsonData = JSON.parse(jsonMatch[1]);
      
      // JSON 이전 내용 (Hook, Introduction 등)
      const beforeJson = script.substring(0, script.indexOf('```json')).trim();
      
      if (beforeJson) {
        // Hook 섹션
        const hookMatch = beforeJson.match(/# Hook[\s\S]*?(?=# Introduction|# Main|$)/);
        if (hookMatch) {
          let hookContent = hookMatch[0].replace(/# Hook.*?\n/, '').trim();
          hookContent = hookContent.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
          hookContent = hookContent.replace(/\[(\d{2}:\d{2})\]/g, '<span style="background: #e3f2fd; color: #1976d2; padding: 2px 8px; border-radius: 4px; font-weight: 600;">$1</span>');
          hookContent = hookContent.replace(/\n/g, '<br>');
          
          html += `
            <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; border-radius: 12px; padding: 1.5rem; margin-bottom: 1.5rem; box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);">
              <div style="font-size: 0.875rem; font-weight: 600; margin-bottom: 0.75rem; opacity: 0.95;">🎬 HOOK (첫 10초)</div>
              <div style="font-size: 0.9375rem; line-height: 1.8;">${hookContent}</div>
            </div>
          `;
        }
        
        // Introduction 섹션
        const introMatch = beforeJson.match(/# Introduction[\s\S]*?(?=# Main|# Hook|$)/);
        if (introMatch) {
          let introContent = introMatch[0].replace(/# Introduction.*?\n/, '').trim();
          introContent = introContent.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
          introContent = introContent.replace(/\[(\d{2}:\d{2})\]/g, '<span style="background: #e3f2fd; color: #1976d2; padding: 2px 8px; border-radius: 4px; font-weight: 600;">$1</span>');
          introContent = introContent.replace(/\n/g, '<br>');
          
          html += `
            <div style="background: #f0fdf4; border: 2px solid #86efac; border-radius: 12px; padding: 1.5rem; margin-bottom: 1.5rem;">
              <div style="font-size: 0.875rem; font-weight: 600; color: #15803d; margin-bottom: 0.75rem;">👋 INTRODUCTION</div>
              <div style="font-size: 0.9375rem; line-height: 1.8; color: #166534;">${introContent}</div>
            </div>
          `;
        }
      }
      
      // 메인 콘텐츠 (JSON 파싱)
      if (jsonData.main && Array.isArray(jsonData.main)) {
        jsonData.main.forEach((section: any, idx: number) => {
          html += `
            <div style="background: #ffffff; border-radius: 12px; padding: 1.5rem; margin-bottom: 1.5rem; box-shadow: 0 2px 8px rgba(0,0,0,0.08); border-left: 4px solid #42a5f5;">
              <h3 style="margin: 0 0 1rem 0; font-size: 1.125rem; font-weight: 700; color: #2c3e50;">
                <span style="color: #42a5f5;">#${idx + 1}</span> ${section.section || ''}
              </h3>
          `;
          
          if (section.timestamp) {
            html += `<div style="display: inline-block; background: #e3f2fd; color: #1976d2; padding: 0.375rem 0.875rem; border-radius: 6px; font-size: 0.875rem; font-weight: 600; margin-bottom: 1rem;">⏱️ ${section.timestamp}</div>`;
          }
          
          if (section.content) {
            html += `<p style="color: #374151; font-size: 0.9375rem; line-height: 1.8; margin: 0.75rem 0; white-space: pre-wrap;">${section.content}</p>`;
          }
          
          const suggestions = section.visualSuggestions || section.visual_suggestions;
          if (suggestions && Array.isArray(suggestions) && suggestions.length > 0) {
            html += `
              <div style="margin-top: 1rem;">
                <div style="font-size: 0.875rem; font-weight: 600; color: #6b7280; margin-bottom: 0.5rem;">🎨 시각 제안</div>
                <div style="display: flex; flex-wrap: wrap; gap: 0.5rem;">
            `;
            suggestions.forEach((visual: string) => {
              html += `<span style="background: #f3f4f6; padding: 0.5rem 0.875rem; border-radius: 6px; font-size: 0.8125rem; color: #4b5563; border: 1px solid #e5e7eb;">${visual}</span>`;
            });
            html += `</div></div>`;
          }
          
          const points = section.keyPoints || section.key_points;
          if (points && Array.isArray(points) && points.length > 0) {
            html += `
              <div style="margin-top: 1rem; background: #fef3c7; padding: 1rem; border-radius: 8px; border: 1px solid #fcd34d;">
                <div style="font-size: 0.875rem; font-weight: 600; color: #92400e; margin-bottom: 0.5rem;">💡 핵심 포인트</div>
                <ul style="margin: 0; padding-left: 1.5rem; color: #78350f; font-size: 0.875rem; line-height: 1.6;">
            `;
            points.forEach((point: string) => {
              html += `<li style="margin-bottom: 0.25rem;">${point}</li>`;
            });
            html += `</ul></div>`;
          }
          
          html += `</div>`;
        });
      }
      
      // JSON 이후 내용 (Call to Action, Outro)
      const afterJson = script.substring(script.indexOf('```') + jsonMatch[0].length).trim();
      
      if (afterJson) {
        const ctaMatch = afterJson.match(/# Call to Action[\s\S]*?(?=# Outro|$)/);
        if (ctaMatch) {
          let ctaContent = ctaMatch[0].replace(/# Call to Action.*?\n/, '').trim();
          ctaContent = ctaContent.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
          ctaContent = ctaContent.replace(/\[(\d{2}:\d{2})\]/g, '<span style="background: #e3f2fd; color: #1976d2; padding: 2px 8px; border-radius: 4px; font-weight: 600;">$1</span>');
          ctaContent = ctaContent.replace(/\n/g, '<br>');
          
          html += `
            <div style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); color: #ffffff; border-radius: 12px; padding: 1.5rem; margin-bottom: 1.5rem; box-shadow: 0 4px 12px rgba(245, 87, 108, 0.3);">
              <div style="font-size: 0.875rem; font-weight: 600; margin-bottom: 0.75rem; opacity: 0.95;">🎯 CALL TO ACTION</div>
              <div style="font-size: 0.9375rem; line-height: 1.8;">${ctaContent}</div>
            </div>
          `;
        }
        
        const outroMatch = afterJson.match(/# Outro[\s\S]*$/);
        if (outroMatch) {
          let outroContent = outroMatch[0].replace(/# Outro.*?\n/, '').trim();
          outroContent = outroContent.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
          outroContent = outroContent.replace(/\[(\d{2}:\d{2})\]/g, '<span style="background: #e3f2fd; color: #1976d2; padding: 2px 8px; border-radius: 4px; font-weight: 600;">$1</span>');
          outroContent = outroContent.replace(/\n/g, '<br>');
          
          html += `
            <div style="background: #fef2f2; border: 2px solid #fecaca; border-radius: 12px; padding: 1.5rem;">
              <div style="font-size: 0.875rem; font-weight: 600; color: #991b1b; margin-bottom: 0.75rem;">👋 OUTRO</div>
              <div style="font-size: 0.9375rem; line-height: 1.8; color: #7f1d1d;">${outroContent}</div>
            </div>
          `;
        }
      }
      
      return html;
      
    } catch (e) {
      console.error('JSON 파싱 실패:', e);
      return script.replace(/\n/g, '<br>');
    }
  }
  
  // JSON이 없으면 기본 포맷팅
  let formatted = script;
  formatted = formatted.replace(/# (.+)/g, '<h1 style="font-size: 1.5rem; font-weight: 700; color: #1f2937; margin: 1.5rem 0 1rem 0;">$1</h1>');
  formatted = formatted.replace(/## (.+)/g, '<h2 style="font-size: 1.25rem; font-weight: 600; color: #374151; margin: 1.25rem 0 0.75rem 0;">$2</h2>');
  formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight: 700; color: #1f2937;">$1</strong>');
  formatted = formatted.replace(/\[(\d{2}:\d{2})\]/g, '<span style="background: #e3f2fd; color: #1976d2; padding: 2px 8px; border-radius: 4px; font-weight: 600;">$1</span>');
  formatted = formatted.replace(/\n\n/g, '</p><p style="margin: 1rem 0;">');
  formatted = '<p style="margin: 1rem 0;">' + formatted + '</p>';
  
  return formatted;
}

interface ContentEditorProps {
  initialContent?: string;
  contentType: 'blog' | 'youtube';
  keyword?: string;
  onContentChange?: (content: string) => void;
  onSave?: (content: string, metadata: any) => void;
}

export const ContentEditor: React.FC<ContentEditorProps> = ({
  initialContent = '',
  contentType,
  keyword = '',
  onContentChange,
  onSave
}) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [seoScore, setSeoScore] = useState(0);
  const [readabilityScore, setReadabilityScore] = useState(0);
  const [wordCount, setWordCount] = useState(0);
  const [showSEOPanel, setShowSEOPanel] = useState(false);
  const [internalLinks, setInternalLinks] = useState<InternalLinkSuggestion[]>([]);
  const [imageSuggestions, setImageSuggestions] = useState<ImageSuggestion[]>([]);
  const [seoImprovements, setSeoImprovements] = useState<SEOImprovement[]>([]);
  const [loadingSEO, setLoadingSEO] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        // StarterKit에 포함된 link 제거
        link: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'editor-link',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'editor-image',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: initialContent,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const text = editor.getText();
      onContentChange?.(html);
      
      // 실시간 분석
      analyzeSEO(text, html);
      analyzeReadability(text);
      updateWordCount(text);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none focus:outline-none youtube-script-editor',
        style: 'min-height: 500px; padding: 1.5rem;',
      },
    },
  });

  // 초기 콘텐츠 설정 (isEmpty 체크 제거하여 항상 업데이트)
  useEffect(() => {
    if (editor && initialContent) {
      if (contentType === 'youtube') {
        // YouTube 스크립트: 간단한 HTML로 변환 (TipTap이 인식 가능하도록)
        let html = '';
        const lines = initialContent.split('\n');

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];

          // 빈 줄
          if (!line.trim()) {
            html += '<p><br></p>';
            continue;
          }

          // 헤더 변환 (# Hook → <h1>)
          if (line.match(/^#{1,3}\s+/)) {
            const level = (line.match(/^(#{1,3})/)?.[1].length || 1);
            const text = line.replace(/^#{1,3}\s+/, '').trim();
            html += `<h${level}>${text}</h${level}>`;
            continue;
          }

          // JSON 블록 시작
          if (line.trim() === '```json') {
            let jsonLines = [];
            i++;
            while (i < lines.length && lines[i].trim() !== '```') {
              jsonLines.push(lines[i]);
              i++;
            }
            // JSON을 pre/code로 감싸기
            const jsonText = jsonLines.join('\n');
            html += `<pre><code>${jsonText}</code></pre>`;
            continue;
          }

          // 구분선
          if (line.trim() === '---') {
            html += '<hr>';
            continue;
          }

          // 일반 텍스트 (굵은 글씨 변환)
          let content = line;
          // **텍스트** → <strong>텍스트</strong>
          content = content.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
          html += `<p>${content}</p>`;
        }

        console.log('[ContentEditor] YouTube HTML 변환:', html.substring(0, 800));
        console.log('[ContentEditor] 총 라인 수:', lines.length);

        // 기존 내용 완전히 교체
        editor.commands.setContent(html);
      } else {
        // 블로그: 원본 텍스트 그대로 로드
        const plainText = initialContent.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        editor.commands.setContent(`<p>${plainText.split('\n').join('</p><p>')}</p>`);
      }
    }
  }, [editor, initialContent, contentType]);

  const updateWordCount = useCallback((text: string) => {
    const words = text.split(/\s+/).filter(w => w.length > 0);
    setWordCount(words.length);
  }, []);

  const analyzeSEO = useCallback(async (text: string, html: string) => {
    if (!keyword) return;
    
    let score = 0;
    const keywordLower = keyword.toLowerCase();
    const textLower = text.toLowerCase();
    
    // 키워드 밀도 체크
    const keywordCount = (textLower.match(new RegExp(keywordLower, 'g')) || []).length;
    const words = textLower.split(/\s+/).filter(w => w.length > 0);
    const wordCount = words.length;
    const density = wordCount > 0 ? (keywordCount / wordCount) * 100 : 0;
    
    // SEO 점수 계산
    if (density >= 1 && density <= 3) score += 30; // 적절한 키워드 밀도
    if (textLower.includes(keywordLower)) score += 20; // 키워드 포함
    if (text.length > 500) score += 20; // 충분한 길이
    if (html.includes('<h1>')) score += 15; // H1 존재
    const h2Count = (html.match(/<h2>/g) || []).length;
    if (h2Count >= 2) score += 15; // H2 2개 이상
    
    // 이미지 ALT 텍스트 체크
    const images: string[] = html.match(/<img[^>]*>/g) || [];
    const imagesWithAlt = images.filter((img: string) => img.includes('alt='));
    if (images.length > 0 && imagesWithAlt.length === images.length) {
      score += 10; // 모든 이미지에 ALT 텍스트
    }
    
    setSeoScore(Math.min(100, score));
  }, [keyword]);

  const analyzeReadability = useCallback(async (text: string) => {
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const words = text.split(/\s+/).filter(w => w.length > 0);
    const avgWordsPerSentence = sentences.length > 0 ? words.length / sentences.length : 0;
    
    let score = 100;
    if (avgWordsPerSentence > 25) score -= 20; // 문장이 너무 김
    if (avgWordsPerSentence < 10) score -= 10; // 문장이 너무 짧음
    if (words.length < 100) score -= 30; // 내용이 너무 짧음
    
    // 문단 길이 체크
    const paragraphs = text.split(/\n\n+/).filter(p => p.trim().length > 0);
    const avgParagraphLength = paragraphs.length > 0 
      ? paragraphs.reduce((sum, p) => sum + p.split(/\s+/).length, 0) / paragraphs.length 
      : 0;
    if (avgParagraphLength > 150) score -= 10; // 문단이 너무 김
    
    setReadabilityScore(Math.max(0, score));
  }, []);

  const handleSave = useCallback(() => {
    if (!editor) return;
    
    const html = editor.getHTML();
    const text = editor.getText();
    const metadata = {
      wordCount,
      readingTime: Math.ceil(wordCount / 200),
      seoScore,
      readabilityScore,
      lastSaved: new Date().toISOString()
    };
    
    onSave?.(html, metadata);
  }, [editor, wordCount, seoScore, readabilityScore, onSave]);

  const togglePreview = useCallback(() => {
    setIsPreviewMode(!isPreviewMode);
  }, [isPreviewMode]);

  const insertImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('이미지 URL을 입력하세요:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  const insertLink = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('링크 URL을 입력하세요:');
    if (url) {
      const text = window.prompt('링크 텍스트를 입력하세요:', editor.state.doc.textBetween(
        editor.state.selection.from,
        editor.state.selection.to,
        ' '
      ));
      if (text) {
        editor.chain().focus().insertContent(`<a href="${url}">${text}</a>`).run();
      }
    }
  }, [editor]);

  const insertTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  const loadSEOSuggestions = useCallback(async () => {
    if (!editor || !keyword) return;
    
    setLoadingSEO(true);
    try {
      const html = editor.getHTML();
      const suggestions = await seoService.getSuggestions(keyword, html, {});
      setInternalLinks(suggestions.internalLinks);
      setImageSuggestions(suggestions.imageSuggestions);
      setSeoImprovements(suggestions.improvements);
    } catch (error) {
      console.error('SEO suggestions error:', error);
    } finally {
      setLoadingSEO(false);
    }
  }, [editor, keyword]);

  const insertInternalLink = useCallback((link: InternalLinkSuggestion) => {
    if (!editor) return;
    const selectedText = editor.state.doc.textBetween(
      editor.state.selection.from,
      editor.state.selection.to,
      ' '
    ) || link.title;
    
    editor.chain().focus().insertContent(`<a href="${link.url}">${selectedText || link.title}</a>`).run();
  }, [editor]);

  const insertSuggestedImage = useCallback((suggestion: ImageSuggestion) => {
    if (!editor) return;
    editor.chain().focus().setImage({ 
      src: suggestion.url, 
      alt: suggestion.alt 
    }).run();
  }, [editor]);

  if (!editor) {
    return <div>에디터 로딩 중...</div>;
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#fff',
      borderRadius: 12,
      border: '1px solid rgba(0,0,0,0.06)',
      boxShadow: '0 6px 16px rgba(66,165,245,0.10)'
    }}>
      {/* 툴바 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1rem',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        flexWrap: 'wrap',
        gap: '0.5rem'
      }}>
        {/* 포맷 버튼 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'is-active' : ''}
            style={{
              padding: '0.5rem',
              background: editor.isActive('bold') ? '#42a5f5' : '#f0f0f0',
              color: editor.isActive('bold') ? '#fff' : '#333',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
            title="굵게"
          >
            B
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'is-active' : ''}
            style={{
              padding: '0.5rem',
              background: editor.isActive('italic') ? '#42a5f5' : '#f0f0f0',
              color: editor.isActive('italic') ? '#fff' : '#333',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontStyle: 'italic'
            }}
            title="기울임"
          >
            I
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
            style={{
              padding: '0.5rem',
              background: editor.isActive('heading', { level: 1 }) ? '#42a5f5' : '#f0f0f0',
              color: editor.isActive('heading', { level: 1 }) ? '#fff' : '#333',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
            title="제목 1"
          >
            H1
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
            style={{
              padding: '0.5rem',
              background: editor.isActive('heading', { level: 2 }) ? '#42a5f5' : '#f0f0f0',
              color: editor.isActive('heading', { level: 2 }) ? '#fff' : '#333',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
            title="제목 2"
          >
            H2
          </button>
          <button
            onClick={insertLink}
            style={{
              padding: '0.5rem',
              background: '#f0f0f0',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer'
            }}
            title="링크 삽입"
          >
            🔗
          </button>
          <button
            onClick={insertImage}
            style={{
              padding: '0.5rem',
              background: '#f0f0f0',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer'
            }}
            title="이미지 삽입"
          >
            🖼️
          </button>
          <button
            onClick={insertTable}
            style={{
              padding: '0.5rem',
              background: '#f0f0f0',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer'
            }}
            title="테이블 삽입"
          >
            📊
          </button>
          <button
            onClick={() => {
              setShowSEOPanel(!showSEOPanel);
              if (!showSEOPanel) {
                loadSEOSuggestions();
              }
            }}
            style={{
              padding: '0.5rem',
              background: showSEOPanel ? '#42a5f5' : '#f0f0f0',
              color: showSEOPanel ? '#fff' : '#333',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer'
            }}
            title="SEO 제안"
          >
            ⚡
          </button>
        </div>

        {/* 점수 표시 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: '#666' }}>단어:</span>
            <span style={{ fontWeight: 600 }}>{wordCount}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: '#666' }}>SEO:</span>
            <span style={{
              fontWeight: 600,
              color: seoScore >= 80 ? '#00b894' : seoScore >= 60 ? '#fdcb6e' : '#d63031'
            }}>
              {seoScore}/100
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: '#666' }}>가독성:</span>
            <span style={{
              fontWeight: 600,
              color: readabilityScore >= 80 ? '#00b894' : readabilityScore >= 60 ? '#fdcb6e' : '#d63031'
            }}>
              {readabilityScore}/100
            </span>
          </div>
        </div>

        {/* 액션 버튼 */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={togglePreview}
            style={{
              padding: '0.5rem 1rem',
              background: isPreviewMode ? '#42a5f5' : '#f0f0f0',
              color: isPreviewMode ? '#fff' : '#333',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            {isPreviewMode ? '편집' : '미리보기'}
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '0.5rem 1rem',
              background: '#42a5f5',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 600
            }}
          >
            저장
          </button>
        </div>
      </div>

      {/* 에디터/미리보기 영역 */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {!isPreviewMode ? (
          <div style={{ flex: 1, overflow: 'auto', display: 'flex' }}>
            <div style={{ flex: showSEOPanel ? '1 1 70%' : '1', overflow: 'auto' }}>
              <EditorContent editor={editor} />
            </div>
            {showSEOPanel && (
              <div style={{
                width: '300px',
                borderLeft: '1px solid rgba(0,0,0,0.06)',
                background: '#f8f9fa',
                overflow: 'auto',
                padding: '1rem'
              }}>
                <h3 style={{ marginTop: 0, fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>
                  SEO 제안
                </h3>
                
                {loadingSEO ? (
                  <div>로딩 중...</div>
                ) : (
                  <>
                    {/* SEO 개선 제안 */}
                    {seoImprovements.length > 0 && (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                          개선 제안
                        </h4>
                        {seoImprovements.map((improvement, idx) => (
                          <div
                            key={idx}
                            style={{
                              padding: '0.75rem',
                              marginBottom: '0.5rem',
                              background: improvement.priority === 'high' ? '#fff3cd' : 
                                         improvement.priority === 'medium' ? '#d1ecf1' : '#d4edda',
                              borderRadius: 6,
                              fontSize: '0.8125rem'
                            }}
                          >
                            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                              {improvement.message}
                            </div>
                            <div style={{ color: '#666', fontSize: '0.75rem' }}>
                              {improvement.suggestion}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* 내부 링크 제안 */}
                    {internalLinks.length > 0 && (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                          내부 링크 제안
                        </h4>
                        {internalLinks.map((link, idx) => (
                          <button
                            key={idx}
                            onClick={() => insertInternalLink(link)}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              marginBottom: '0.5rem',
                              background: '#fff',
                              border: '1px solid #ddd',
                              borderRadius: 4,
                              cursor: 'pointer',
                              textAlign: 'left',
                              fontSize: '0.8125rem'
                            }}
                          >
                            <div style={{ fontWeight: 600 }}>{link.title}</div>
                            <div style={{ color: '#666', fontSize: '0.75rem' }}>{link.keyword}</div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* 이미지 제안 */}
                    {imageSuggestions.length > 0 && (
                      <div>
                        <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                          이미지 제안
                        </h4>
                        {imageSuggestions.map((img, idx) => (
                          <div
                            key={idx}
                            style={{
                              marginBottom: '0.75rem',
                              padding: '0.5rem',
                              background: '#fff',
                              borderRadius: 6,
                              border: '1px solid #ddd'
                            }}
                          >
                            <img
                              src={img.url}
                              alt={img.alt}
                              style={{
                                width: '100%',
                                height: 'auto',
                                borderRadius: 4,
                                marginBottom: '0.5rem'
                              }}
                            />
                            <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.5rem' }}>
                              {img.alt}
                            </div>
                            <button
                              onClick={() => insertSuggestedImage(img)}
                              style={{
                                width: '100%',
                                padding: '0.25rem',
                                background: '#42a5f5',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              이미지 삽입
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              padding: '1.5rem',
              overflow: 'auto',
              background: '#fafafa'
            }}
            dangerouslySetInnerHTML={{ __html: editor.getHTML() }}
          />
        )}
      </div>

      <style>{`
        .ProseMirror {
          outline: none;
          padding: 1.5rem;
          min-height: 500px;
        }
        .ProseMirror p {
          margin: 0.5rem 0;
        }
        .ProseMirror h1 {
          font-size: 2rem;
          font-weight: 700;
          margin: 1rem 0;
        }
        .ProseMirror h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0.75rem 0;
        }
        .ProseMirror h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0.5rem 0;
        }
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
        }
        .ProseMirror table {
          border-collapse: collapse;
          margin: 1rem 0;
          width: 100%;
        }
        .ProseMirror table td,
        .ProseMirror table th {
          border: 1px solid #ddd;
          padding: 0.5rem;
        }
        .ProseMirror table th {
          background: #f0f0f0;
          font-weight: 600;
        }
        .editor-link {
          color: #42a5f5;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};
